import { AlertTriangle, Mail, Phone, MapPin, Clock, Briefcase } from "lucide-react";
import ScoreBadge from "@/components/ScoreBadge";

function daysIn(iso) {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
}

// Floating candidate quick-view shown on kanban-card hover (smart pipeline).
export default function CandidateHoverCard({ app, rect }) {
    if (!app || !rect) return null;
    const c = app.candidate || {};
    const W = 320;
    let left = rect.right + 10;
    if (left + W > window.innerWidth - 8) left = Math.max(8, rect.left - W - 10);
    let top = Math.min(rect.top, window.innerHeight - 340);
    if (top < 8) top = 8;
    const stageAge = daysIn(app.stage_entered_at || app.updated_at || app.created_at);
    const flags = app.analysis?.red_flags || [];
    const votes = app.vote_summary?.counts || null;
    return (
        <div
            className="fixed z-[80] bg-white border-2 border-ikb shadow-xl p-4 pointer-events-none animate-in fade-in duration-150"
            style={{ left, top, width: W }}
            data-testid={`candidate-hovercard-${app.id}`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="font-display font-bold text-base truncate">{c.name || "Unknown"}</div>
                    <div className="text-xs text-foreground/80 truncate mt-0.5">
                        {c.current_title || "—"}{c.current_company ? ` · ${c.current_company}` : ""}
                    </div>
                </div>
                <ScoreBadge score={app.match_score || 0} size="sm" />
            </div>

            <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px] text-foreground/80">
                <span className="inline-flex items-center gap-1.5 truncate"><Clock className="w-3 h-3 text-ikb shrink-0" /> {app.stage || "Applied"}{stageAge !== null ? ` · ${stageAge}d in stage` : ""}</span>
                {typeof c.total_experience_years === "number" && c.total_experience_years > 0 && (
                    <span className="inline-flex items-center gap-1.5"><Briefcase className="w-3 h-3 text-ikb shrink-0" /> {c.total_experience_years} yrs exp</span>
                )}
                {c.location && <span className="inline-flex items-center gap-1.5 truncate"><MapPin className="w-3 h-3 text-ikb shrink-0" /> {c.location}</span>}
                {c.email && <span className="inline-flex items-center gap-1.5 truncate"><Mail className="w-3 h-3 text-ikb shrink-0" /> {c.email}</span>}
                {c.phone && <span className="inline-flex items-center gap-1.5 truncate"><Phone className="w-3 h-3 text-ikb shrink-0" /> {c.phone}</span>}
            </div>

            {(c.skills || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                    {c.skills.slice(0, 6).map((s) => (
                        <span key={s} className="text-[10px] font-mono uppercase tracking-wider border border-border bg-secondary/50 px-1.5 py-0.5">{s}</span>
                    ))}
                    {c.skills.length > 6 && <span className="text-[10px] font-mono text-foreground/60">+{c.skills.length - 6}</span>}
                </div>
            )}

            {flags.length > 0 && (
                <div className="mt-3 border-t border-border pt-2 space-y-1">
                    {flags.slice(0, 3).map((f, i) => (
                        <div key={i} className="flex items-start gap-1.5 text-[11px] text-rose-700">
                            <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                            <span className="line-clamp-1">{typeof f === "string" ? f : f?.text || f?.flag || ""}</span>
                        </div>
                    ))}
                </div>
            )}

            {votes && Object.keys(votes).length > 0 && (
                <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-foreground/70">
                    Votes: {Object.entries(votes).map(([k, v]) => `${k} ${v}`).join(" · ")}
                </div>
            )}
        </div>
    );
}
